import React, { useEffect, useState } from "react";
import { InputAdornment, OutlinedInput } from "@mui/material";
import { UpArrowIcon, DownArrowIcon } from "../../providers/RexIconsSVG/RexIconsSVG";
import useStyles from "./styles";
import { getDateComponent, isSameDayUtil, setDateComponent, setDateComponents, setHoursAndMinutes } from "../../utils/dateFnsUtils";

const timeRange = [...Array(24).keys()];
const minuteRange = [...Array(60).keys()];

const Arrows = ({ handleArrowClick }) => {
  const classes = useStyles();
  return (
    <InputAdornment position="start" className={classes.arrowWrapper}>
      <UpArrowIcon
        role="up"
        viewBox="-6 -5 24 10"
        onClick={({ target }) => {
          handleArrowClick(target.getAttribute("role") || "up");
        }}
        className={classes.arrow}
      />
      <DownArrowIcon
        role="down"
        viewBox="-6 6 24 10"
        onClick={({ target }) => {
          handleArrowClick(target.getAttribute("role") || "down");
        }}
        className={classes.arrow}
      />
    </InputAdornment>
  );
};

const TimePicker = ({ date, onChange }) => {
  const classes = useStyles();
  const [time, setTime] = useState(date ? new Date(date) : new Date());
  useEffect(() => {
    if (!date) {
      //set a default date time if value received is null
      const newTime = setHoursAndMinutes(new Date(), 0, 0);
      setTime(newTime);
    } else if (isSameDayUtil(time, new Date(date))) {
      //only time has changed
      setTime(new Date(date));
    } else {
      //if day has changed do not modify the time
      const newDate = new Date(date);
      const newTime = setDateComponents(time, {
        date: newDate.getDate(),
        month: newDate.getMonth(),
        year: newDate.getFullYear(),
      });
      setTime(newTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

const handleTimeChangeOnArrowClick = (direction, unit) => {
  let newUnitTime = getDateComponent(time, unit);
  
  let range = [];
  if (unit === "minutes") {
    range = minuteRange;
  } else {
    range = timeRange;
  }
  
  let index =
    direction === "up"
      ? range.indexOf(newUnitTime) + 1
      : range.indexOf(newUnitTime) - 1;
  
  index = index === -1 ? range.length - 1 : index;
  index = index % range.length;
  
  // Get the new value directly from the range
  const newValue = range[index];
  
  // Update the time with the new value
  const updatedTime = setDateComponent(time, unit, newValue);
  setTime(updatedTime);
  onChange(updatedTime);
};

  /*Handler for changing input on keyboard typing*/
  const handleTimeInputChange = ({ target: { value } }, unit) => {
    /*Check if input is a number*/
    const re = /^[0-9\b]+$/;

    if (re.test(value)) {
      const endRange = unit === "hours" ? 24 : 60;
      const numValue = parseInt(value, 10);

      if (numValue >= 0 && numValue < endRange) {
        // Create a new date with the updated unit
        const updatedTime = setDateComponent(time, unit, numValue);

        // Update the state
        setTime(updatedTime);

        // Call the onChange handler with the new date
        onChange(updatedTime);
      }
    } else if (value === "") {
      // If the input is empty, set hours to 0
      const updatedTime = setDateComponent(time, "hours", 0);

      // Update the state
      setTime(updatedTime);

      // Call the onChange handler with the new date
      onChange(updatedTime);
    }
  };

  return (
    <div className={classes.timePickerContainer}>
      <OutlinedInput
        type="text"
        inputProps={{
          "data-testid": "hours",
        }}
        className={classes.numericInputFormControl}
        endAdornment={
          <Arrows
            handleArrowClick={(direction) => {
              handleTimeChangeOnArrowClick(direction, "hours");
            }}
          />
        }
        onChange={(e) => handleTimeInputChange(e, "hours")}
        value={String(time.getHours()).padStart(2, "0")}
      />
      <OutlinedInput
        type="text"
        inputProps={{
          "data-testid": "minutes",
        }}
        className={classes.numericInputFormControl}
        endAdornment={
          <Arrows
            handleArrowClick={(direction) => {
              handleTimeChangeOnArrowClick(direction, "minutes");
            }}
          />
        }
        onChange={(e) => handleTimeInputChange(e, "minutes")}
        value={String(time.getMinutes()).padStart(2, "0")}
      />
    </div>
  );
};

export default TimePicker;
